import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import type { Grid, GridValues } from "@/types/grid";
import { Button } from "@/components/button";
import { ConfirmModal } from "@/components/modal";
import { ValidateGridModal } from "@/components/Grid";
import { match } from "ts-pattern";
import { MagnifyingGlass, Image as ImageIcon, X, CaretLeft, CaretRight, Trash } from "@phosphor-icons/react";
import {
  appContainer,
  appContainerFull,
  appHeader,
  appHeaderImports,
  appTitle,
  appSubtitle,
  emptyState,
  loading as loadingStyle,
  error as errorStyle,
} from "@/styles/shared";

const filterBar = css`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  align-items: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const searchInputContainer = css`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 300px;

  @media (max-width: 640px) {
    max-width: none;
  }
`;

const searchInput = css`
  width: 100%;
  padding: 10px 16px 10px 40px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const searchIcon = css`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
`;

const statusSelect = css`
  padding: 10px 16px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--input-bg);
  cursor: pointer;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
  }
`;

const gridsGrid = css`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const gridCard = css`
  background: var(--container-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px var(--shadow-medium);
    border-color: var(--input-border-focus);
  }
`;

const gridCardTop = css`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const gridPlayedCount = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding-top: 8px;
  border-top: 1px solid var(--color-border);
`;

const gridThumbnail = css`
  flex-shrink: 0;
  width: 80px;
  height: 60px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background: var(--color-bg-warm);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const gridThumbnailImg = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const gridThumbnailPlaceholder = css`
  color: var(--text-accent);
  font-size: 24px;
`;

const gridInfo = css`
  flex: 1;
  min-width: 0;
`;

const gridActions = css`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const deleteButton = css`
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--error-bg);
    color: var(--error-text);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const gridIdentifier = css`
  font-family: var(--font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const gridNoIdentifier = css`
  font-style: italic;
  color: var(--text-secondary);
`;

const statusBadge = css`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const statusPending = css`
  background: var(--color-bg-warm);
  color: var(--text-accent);
`;

const statusConfirmed = css`
  background: var(--success-bg);
  color: var(--success-text);
`;

const statusCancelled = css`
  background: var(--error-bg);
  color: var(--error-text);
`;

function getStatusStyles(status: Grid["status"]) {
  const variant = match(status)
    .with("Confirmed", () => statusConfirmed)
    .with("Cancelled", () => statusCancelled)
    .with("Deleted", () => statusCancelled)
    .otherwise(() => statusPending);
  return [statusBadge, variant];
}

const paginationBar = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`;

const paginationIndicator = css`
  font-size: 0.9rem;
  color: var(--text-secondary);
  font-weight: 500;
  min-width: 100px;
  text-align: center;
`;

const photoPreviewOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const photoPreviewModal = css`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
`;

const photoPreviewImg = css`
  max-width: 90vw;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 12px;
`;

const photoPreviewClose = css`
  position: absolute;
  top: -12px;
  right: -12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: var(--container-bg);
  color: var(--text-primary);
  font-size: 1.4rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px var(--shadow-medium);
`;

const uploadModalOverlay = css`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const uploadModalOverlayBusy = css`
  cursor: wait;
`;

const uploadModal = css`
  background: var(--container-bg);
  border-radius: 16px;
  box-shadow: 0 8px 32px var(--shadow-dark);
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const uploadModalTitle = css`
  font-size: 1.25rem;
  font-weight: 400;
  color: var(--text-primary);
  margin: 0;
`;

const uploadDropzone = css`
  border: 2px dashed var(--input-border);
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  background: var(--color-bg-warm);
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: var(--dropdown-hover);
    border-color: var(--input-border-focus);
  }
`;

const uploadDropzoneInput = css`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const uploadDropzoneText = css`
  color: var(--text-secondary);
  font-size: 1rem;
`;

const uploadFileList = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow: auto;
`;

const uploadFileListTitle = css`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const uploadFileListUl = css`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: var(--color-bg-warm);
  border-radius: 8px;
  padding: 8px;
`;

const uploadFileListLi = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  background: var(--container-bg);
`;

const uploadFileListName = css`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const uploadFileListRemove = css`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  background: var(--error-bg);
  color: var(--error-text);
  border-radius: 6px;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover {
    opacity: 0.9;
  }
`;

const uploadModalError = css`
  padding: 12px;
  border-radius: 8px;
  background: var(--error-bg);
  color: var(--error-text);
  font-size: 0.9rem;
`;

const uploadModalActions = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
`;

const importsUploadBtn = css`
  flex-shrink: 0;
`;

const errorWithMargin = css`
  margin-bottom: 24px;
`;

const PAGE_SIZE = 20;

const IMAGE_BASE_URL = process.env.BUN_PUBLIC_CDN_URL;

function imageUrl(fileUrl: string): string {
  if (!fileUrl) return "";
  return `${IMAGE_BASE_URL}/${fileUrl}`;
}

type GridStatus = "WaitingForUpload" | "Uploaded" | "NumbersExtracted" | "Confirmed" | "Cancelled";

export function GridsPage() {
  const { t } = useTranslation();
  const [grids, setGrids] = useState<Grid[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Filters
  const [identifierFilter, setIdentifierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<GridStatus | "">("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadModalError, setUploadModalError] = useState<string | null>(null);
  const modalFileInputRef = useRef<HTMLInputElement>(null);

  // Photo preview
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Validation modal (for NumbersExtracted grids)
  const [validatingGrid, setValidatingGrid] = useState<(Grid & { numbers: GridValues; identifier: string }) | null>(null);

  // Delete state
  const [gridToDelete, setGridToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const loadGrids = useCallback(
    (page: number, identifier?: string, status?: GridStatus | "") => {
      setIsLoading(true);
      return trpcClient.listGrids
        .query({
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          identifier: identifier || undefined,
          status: status || undefined,
        })
        .then(({ items, totalCount: count }) => {
          setGrids(items);
          setTotalCount(count);
          setIsLoading(false);
        })
        .catch((e) => {
          setErr(e instanceof Error ? e.message : "Failed to load grids");
          setIsLoading(false);
        });
    },
    [],
  );

  // Load grids when filters or page change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      loadGrids(currentPage, identifierFilter, statusFilter);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [currentPage, identifierFilter, statusFilter, loadGrids]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [identifierFilter, statusFilter]);

  const closeUploadModal = useCallback(() => {
    if (uploading) return;
    setUploadModalOpen(false);
    setPendingFiles([]);
    setUploadModalError(null);
    if (modalFileInputRef.current) modalFileInputRef.current.value = "";
    loadGrids(currentPage, identifierFilter, statusFilter);
  }, [uploading, currentPage, identifierFilter, statusFilter, loadGrids]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (previewUrl) {
        setPreviewUrl(null);
        return;
      }
      if (uploadModalOpen && !uploading) closeUploadModal();
    };
    if (!uploadModalOpen && !previewUrl) return;

    // Lock body scroll when modal/preview is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = originalOverflow;
    };
  }, [uploadModalOpen, uploading, closeUploadModal, previewUrl]);

  const openUploadModal = () => {
    setUploadModalError(null);
    setPendingFiles([]);
    setUploadModalOpen(true);
  };

  const addFiles = useCallback((files: FileList | File[]) => {
    const allowedTypes = ["image/jpeg", "image/png"];
    const list = Array.from(files).filter((f) => allowedTypes.includes(f.type));
    setPendingFiles((prev) => [...prev, ...list]);
  }, []);

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleModalFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files?.length) addFiles(files);
    e.target.value = "";
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const confirmUpload = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    setUploadModalError(null);

    try {
      for (const file of pendingFiles) {
        const { uploadUrl } = await trpcClient.importGrid.mutate({
          filename: file.name,
          contentType: file.type,
        });

        const putResponse = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            ...(file.type && { "Content-Type": file.type }),
          },
        });

        if (!putResponse.ok) {
          const text = await putResponse.text();
          throw new Error(
            `Upload failed for ${file.name}: ${putResponse.status} ${text || putResponse.statusText}`,
          );
        }
      }

      setCurrentPage(0);
      await loadGrids(0, identifierFilter, statusFilter);
      setUploadModalOpen(false);
      setPendingFiles([]);
      setUploadModalError(null);
      if (modalFileInputRef.current) modalFileInputRef.current.value = "";
    } catch (e: unknown) {
      setUploadModalError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const hasPhoto = (grid: Grid): boolean => {
    return Boolean(grid.fileUrl);
  };

  const getIdentifier = (grid: Grid): string | null => {
    if ("identifier" in grid && grid.identifier) {
      return grid.identifier;
    }
    return null;
  };

  const handleDeleteGrid = async () => {
    if (!gridToDelete) return;

    setDeleting(true);
    try {
      await trpcClient.deleteGrid.mutate({ gridId: gridToDelete });
      setGridToDelete(null);
      await loadGrids(currentPage, identifierFilter, statusFilter);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to delete grid");
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading && grids.length === 0) {
    return (
      <div css={appContainer}>
        <div css={loadingStyle}>{t("grids.loading")}</div>
      </div>
    );
  }

  return (
    <div css={[appContainer, appContainerFull]}>
      <header css={[appHeader, appHeaderImports]}>
        <div>
          <h1 css={appTitle}>{t("grids.title")}</h1>
          <p css={appSubtitle}>
            {t("grids.totalCount", { count: totalCount })}
          </p>
        </div>
        <Button variant="primary" css={importsUploadBtn} onClick={openUploadModal}>
          {t("grids.uploadButton")}
        </Button>
      </header>

      {err && <div css={[errorStyle, errorWithMargin]}>{err}</div>}

      {/* Filter Bar */}
      <div css={filterBar}>
        <div css={searchInputContainer}>
          <MagnifyingGlass size={18} css={searchIcon} />
          <input
            type="text"
            css={searchInput}
            placeholder={t("grids.searchByIdentifier")}
            value={identifierFilter}
            onChange={(e) => setIdentifierFilter(e.target.value)}
          />
        </div>
        <select
          css={statusSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as GridStatus | "")}
        >
          <option value="">{t("grids.allStatuses")}</option>
          <option value="WaitingForUpload">{t("gridStatus.WaitingForUpload")}</option>
          <option value="Uploaded">{t("gridStatus.Uploaded")}</option>
          <option value="NumbersExtracted">{t("gridStatus.NumbersExtracted")}</option>
          <option value="Confirmed">{t("gridStatus.Confirmed")}</option>
          <option value="Cancelled">{t("gridStatus.Cancelled")}</option>
        </select>
      </div>

      {grids.length === 0 ? (
        <div css={emptyState}>
          <p>{identifierFilter || statusFilter ? t("grids.noResults") : t("grids.empty")}</p>
        </div>
      ) : (
        <>
          <div css={gridsGrid}>
            {grids.map((grid) => {
              const handleGridClick = () => {
                if (grid.status === "NumbersExtracted" && grid.numbers) {
                  setValidatingGrid({
                    ...grid,
                    numbers: grid.numbers,
                    identifier: grid.identifier ?? "",
                  });
                } else if (hasPhoto(grid)) {
                  setPreviewUrl(imageUrl(grid.fileUrl));
                }
              };
              const isClickable = grid.status === "NumbersExtracted" || hasPhoto(grid);

              return (
              <div key={grid.id} css={gridCard}>
                <div css={gridCardTop}>
                  <div
                    css={gridThumbnail}
                    onClick={handleGridClick}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleGridClick();
                      }
                    }}
                  >
                    {grid.fileUrl ? (
                      <img src={imageUrl(grid.fileUrl)} alt="" css={gridThumbnailImg} />
                    ) : (
                      <ImageIcon size={24} css={gridThumbnailPlaceholder} />
                    )}
                  </div>
                  <div css={gridInfo}>
                    <div css={gridIdentifier}>
                      {getIdentifier(grid) || (
                        <span css={gridNoIdentifier}>{t("grids.noIdentifier")}</span>
                      )}
                    </div>
                    <span css={getStatusStyles(grid.status)}>
                      {t(`gridStatus.${grid.status}`)}
                    </span>
                  </div>
                  <div css={gridActions}>
                    <button
                      type="button"
                      css={deleteButton}
                      onClick={() => setGridToDelete(grid.id)}
                      disabled={deleting}
                      aria-label={t("grids.deleteButton")}
                      title={t("grids.deleteButton")}
                    >
                      <Trash size={20} />
                    </button>
                  </div>
                </div>
                <div css={gridPlayedCount}>
                  {t("grids.playedCount", { count: grid.playedCount ?? 0 })}
                </div>
              </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div css={paginationBar}>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 0}
              >
                <CaretLeft size={14} /> {t("common.previous")}
              </Button>
              <span css={paginationIndicator}>
                {t("grids.pageIndicator", { current: currentPage + 1, total: totalPages })}
              </span>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= totalPages - 1}
              >
                {t("common.next")} <CaretRight size={14} />
              </Button>
            </div>
          )}
        </>
      )}

      {/* Photo Preview Modal */}
      {previewUrl &&
        createPortal(
          <div
            css={photoPreviewOverlay}
            onClick={() => setPreviewUrl(null)}
            role="dialog"
            aria-modal="true"
          >
            <div css={photoPreviewModal} onClick={(e) => e.stopPropagation()}>
              <img src={previewUrl} alt={t("grids.gridPhoto")} css={photoPreviewImg} />
              <button
                type="button"
                css={photoPreviewClose}
                onClick={() => setPreviewUrl(null)}
                aria-label={t("common.close")}
              >
                <X size={20} />
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Validation Modal for NumbersExtracted grids */}
      {validatingGrid && (
        <ValidateGridModal
          grid={validatingGrid}
          onClose={() => setValidatingGrid(null)}
          onVerified={() => {
            setValidatingGrid(null);
            loadGrids(currentPage, identifierFilter, statusFilter);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={gridToDelete !== null}
        onClose={() => setGridToDelete(null)}
        onConfirm={handleDeleteGrid}
        title={t("grids.deleteButton")}
        message={t("grids.confirmDelete")}
        confirmLabel={deleting ? t("grids.deleting") : t("grids.deleteButton")}
        loading={deleting}
        variant="danger"
      />

      {/* Upload Modal */}
      {uploadModalOpen &&
        createPortal(
          <div
            css={[uploadModalOverlay, uploading && uploadModalOverlayBusy]}
            onClick={uploading ? undefined : closeUploadModal}
            role="dialog"
            aria-modal="true"
          >
            <div css={uploadModal} onClick={(e) => e.stopPropagation()}>
              <h2 css={uploadModalTitle}>{t("grids.importTitle")}</h2>

              <div
                css={uploadDropzone}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => !uploading && modalFileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !uploading) {
                    e.preventDefault();
                    modalFileInputRef.current?.click();
                  }
                }}
              >
                <input
                  ref={modalFileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  onChange={handleModalFileInputChange}
                  disabled={uploading}
                  css={uploadDropzoneInput}
                />
                <span css={uploadDropzoneText}>
                  {uploading ? t("grids.dropzoneUploading") : t("grids.dropzone")}
                </span>
              </div>

              {pendingFiles.length > 0 && (
                <div css={uploadFileList}>
                  <span css={uploadFileListTitle}>
                    {t("grids.filesSelected", { count: pendingFiles.length })}
                  </span>
                  <ul css={uploadFileListUl}>
                    {pendingFiles.map((file, index) => (
                      <li key={`${file.name}-${file.size}-${index}`} css={uploadFileListLi}>
                        <span css={uploadFileListName} title={file.name}>
                          {file.name}
                        </span>
                        {!uploading && (
                          <button
                            type="button"
                            css={uploadFileListRemove}
                            onClick={(e) => {
                              e.stopPropagation();
                              removePendingFile(index);
                            }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uploadModalError && <div css={uploadModalError}>{uploadModalError}</div>}

              <div css={uploadModalActions}>
                <Button variant="secondary" onClick={closeUploadModal} disabled={uploading}>
                  {t("common.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmUpload}
                  disabled={uploading || pendingFiles.length === 0}
                >
                  {uploading ? t("grids.dropzoneUploading") : t("grids.confirmImport")}
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
